import { useEffect, useState } from "react";
import { Box, ButtonBase, Tooltip } from "@mui/material";
import Iconify from "@/components/iconify/iconify";

/**
 * An id that copies itself when clicked. Its copy icon shows only while the id
 * is hovered (or focused from the keyboard), so a table of ids doesn't fill
 * with icons. The id and the icon are one button, so either one copies.
 */
export const CopyableId = ({ id }: { id: string }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
    } catch {
      // Clipboard access refused; the id is still there to select by hand.
    }
  };

  return (
    <Tooltip title={copied ? "Copied" : "Copy"} placement="top">
      <ButtonBase
        component="span"
        aria-label={`Copy ${id}`}
        onClick={copy}
        sx={{
          label: "copyable-id",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 0.25,
          borderRadius: 0.5,
          fontFamily: "monospace",
          fontSize: "inherit",
          whiteSpace: "nowrap",
          verticalAlign: "baseline",
          "&:hover": { bgcolor: "action.hover" },
          "&:hover .copy-id-icon, &.Mui-focusVisible .copy-id-icon": {
            opacity: 1,
          },
        }}
      >
        {id}
        <Box
          component="span"
          className="copy-id-icon"
          sx={{
            display: "inline-flex",
            color: copied ? "success.main" : "text.secondary",
            opacity: copied ? 1 : 0,
            transition: "opacity 120ms",
          }}
        >
          <Iconify
            icon={copied ? "mdi:check" : "mdi:content-copy"}
            width={14}
          />
        </Box>
      </ButtonBase>
    </Tooltip>
  );
};
